import { useCallback, useEffect, useMemo, useState } from "react";
import { api, UnauthorizedError } from "@/lib/api";
import type { LogFilters, LogResponse, LogEntry, TimeRange } from "@/lib/types";

/** Preset → Fenster in Sekunden (rollierend ab jetzt). */
const RANGE_SECONDS: Record<Exclude<TimeRange, "custom">, number> = {
  "15m": 15 * 60,
  "1h": 60 * 60,
  "6h": 6 * 60 * 60,
  "24h": 24 * 60 * 60,
  "7d": 7 * 24 * 60 * 60,
};

/** datetime-local Wert ("YYYY-MM-DDTHH:mm", lokale Zeit) → RFC3339 (UTC). */
function localToIso(value: string): string | null {
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
}

/** Baut die /api/logs-Query aus den Filtern inkl. Zeitfenster. */
export function buildLogQueryParams(filters: LogFilters): URLSearchParams {
  const p = new URLSearchParams();
  if (filters.level) {
    p.set("level", filters.level);
    p.set("level_mode", filters.levelMode ?? "minimum");
  }
  if (filters.client) p.set("client", filters.client);
  if (filters.service) p.set("service", filters.service);
  if (filters.host) p.set("host", filters.host);
  if (filters.traceId) p.set("trace_id", filters.traceId);
  if (filters.search) p.set("search", filters.search);
  p.set("limit", String(filters.limit ?? 100));

  if (filters.range && filters.range !== "custom") {
    const sec = RANGE_SECONDS[filters.range];
    p.set("start_time", new Date(Date.now() - sec * 1000).toISOString());
  } else if (filters.range === "custom") {
    const from = filters.from ? localToIso(filters.from) : null;
    const to = filters.to ? localToIso(filters.to) : null;
    if (from) p.set("start_time", from);
    if (to) p.set("end_time", to);
  }
  return p;
}

type QueryResult = {
  logs: LogEntry[];
  /** Gesamtzahl der Treffer im Zeitraum (kann > logs.length sein). */
  total: number | null;
  loading: boolean;
  error: string | null;
  refresh: () => void;
};

/**
 * Zeitraum-Suche über REST (`GET /api/logs`) — das Gegenstück zum
 * Live-Stream: sobald ein Zeitraum gewählt ist, wird der WebSocket
 * pausiert und stattdessen gezielt in der Historie gesucht.
 */
export function useLogQuery(filters: LogFilters, enabled: boolean): QueryResult {
  const [logs, setLogs] = useState<LogEntry[]>([]);
  const [total, setTotal] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Stabiler Abhängigkeits-Key statt Objekt-Identität des Filter-Objekts.
  const queryKey = useMemo(() => buildLogQueryParams(filters).toString(), [filters]);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const resp = await api.get<LogResponse>(`/api/logs?${queryKey}`);
      setLogs(resp.logs);
      setTotal(resp.total);
    } catch (e) {
      if (e instanceof UnauthorizedError) return;
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, [queryKey]);

  useEffect(() => {
    if (!enabled) {
      setLogs([]);
      setTotal(null);
      setError(null);
      return;
    }
    void refresh();
  }, [enabled, refresh]);

  return { logs, total, loading, error, refresh: () => void refresh() };
}
